import React from 'react';
import { 
  HiInformationCircle, 
  HiEye, 
  HiX, 
  HiExclamationCircle, 
  HiCheckCircle, 
  HiClock, 
  HiExternalLink,
  HiTag,
  HiArrowRight,
  HiOutlineBriefcase,
  HiOutlineViewBoards,
  HiOutlineCog
} from "react-icons/hi";
import { toLocalTime } from '../../utils/timeUtils';
import { getEntityTypeDisplay } from '../../utils/entityDisplayUtils';

interface MessageProps {
  data: {
    id: number;
    entity_id: string;
    entity_type: string;
    short_description: string;
    message: string;
    success: boolean;
    created_at: string;
    read: boolean;
    type: string;
  };
  index: number;
  handleClick: (data: any) => void;
  handleDeleteClick: (id: number) => void;
}

export const Message: React.FC<MessageProps> = ({ 
  data: { 
    entity_id, 
    entity_type, 
    short_description, 
    message, 
    success, 
    created_at, 
    read, 
    id,
    type
  }, 
  handleClick, 
  handleDeleteClick 
}) => {
  const hasValidEntityId = entity_id && entity_id !== "0";
  const formattedTime = toLocalTime(created_at, { hour: '2-digit', minute: '2-digit', hour12: true });
  const formattedDate = toLocalTime(created_at, { month: '2-digit', day: '2-digit', year: 'numeric' });

  // Enhanced notification icon with better visual hierarchy
  const getNotificationIcon = () => {
    if (success) {
      return (
        <div className="flex items-center justify-center w-10 h-10 bg-light-status-success-bg dark:bg-green-900/30 rounded-full">
          <HiCheckCircle className="w-5 h-5 text-light-status-success-text dark:text-green-400" />
        </div>
      );
    } else if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 bg-light-status-error-bg dark:bg-red-900/30 rounded-full">
          <HiExclamationCircle className="w-5 h-5 text-light-status-error-text dark:text-red-400" />
        </div>
      );
    } else if (message.toLowerCase().includes('warning')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 bg-light-status-warning-bg dark:bg-yellow-900/30 rounded-full">
          <HiExclamationCircle className="w-5 h-5 text-light-status-warning-text dark:text-yellow-400" />
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-10 h-10 bg-light-status-info-bg dark:bg-blue-900/30 rounded-full">
          <HiInformationCircle className="w-5 h-5 text-light-status-info-text dark:text-blue-400" />
        </div>
      );
    }
  };

  // Enhanced entity type display with better styling
  const getEntityTypeDisplayx = () => {
    switch (entity_type) {
      case 'project':
        return { 
          label: 'Work Unit', 
          color: 'bg-light-status-info-bg dark:bg-purple-900/30 text-light-status-info-text dark:text-purple-300',
          icon: <HiOutlineBriefcase className="w-3 h-3" />
        };
      case 'roadmap':
        return { 
          label: 'Strategy Map', 
          color: 'bg-light-status-info-bg dark:bg-indigo-900/30 text-light-status-info-text dark:text-indigo-300',
          icon: <HiOutlineViewBoards className="w-3 h-3" />
        };
      case 'settings':
        return { 
          label: 'Settings', 
          color: 'bg-light-surfaceSecondary dark:bg-gray-700 text-light-text-secondary dark:text-gray-300',
          icon: <HiOutlineCog className="w-3 h-3" />
        };
      default:
        return { 
          label: entity_type, 
          color: 'bg-light-surfaceSecondary dark:bg-gray-700 text-light-text-secondary dark:text-gray-300',
          icon: <HiTag className="w-3 h-3" />
        };
    }
  };

  const entityDisplay =  getEntityTypeDisplay(entity_type, type);

  const finalEntityDisplay =
    entityDisplay.type == "work_unit"
      ? {
          ...entityDisplay,
          label: entity_type,
          color:
            "bg-light-status-info-bg dark:bg-purple-900/30 text-light-status-info-text dark:text-purple-300",
          icon: HiTag,
        }
      : entityDisplay;
  

  const EntityIcon = finalEntityDisplay.icon;
  

  return (
    <div
      className={`group relative overflow-hidden rounded-xl border transition-all duration-300 ${
        read
          ? "bg-light-surface dark:bg-secondary-dark border-light-border-default dark:border-gray-700 hover:border-light-border-emphasis dark:hover:border-gray-600 hover:bg-light-interactive-hover dark:hover:bg-gray-700/30 hover:shadow-md"
          : "bg-light-status-info-bg dark:bg-blue-900/20 border-light-status-info-border dark:border-blue-800/50 shadow-sm hover:shadow-md hover:border-light-status-info-border dark:hover:border-blue-700 hover:bg-light-status-info-bg/70 dark:hover:bg-blue-900/30"
      }`}
      style={{ maxWidth: "100%" }} // Prevent horizontal expansion
    >
      {/* Enhanced unread indicator */}
      {!read && (
        <div className="absolute top-0 left-0 w-1 h-full bg-primary-light dark:bg-primary-dark"></div>
      )}

      <div className="pt-6 px-6 pb-2" style={{ minWidth: 0 }}>
        {" "}
        {/* Prevent horizontal overflow */}
        {/* Enhanced Header with Better Layout */}
        <div className="flex items-start justify-between gap-4 ">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {getNotificationIcon()}

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <h3
                      className={`text-lg font-semibold ${
                        read
                          ? "text-light-text-primary dark:text-white"
                          : "text-light-status-info-text dark:text-blue-100"
                      }`}
                    >
                      {message}
                    </h3>

                    {/* Enhanced entity type badge */}
                    {entity_type && (
                      <div className="flex items-center gap-2 ">
                        <div
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${entityDisplay.chipClass}`}
                        >
                          <EntityIcon />
                          <span className='uppercase'>
                            {entityDisplay.label == "WORK UNIT"
                              ? entity_type
                              : entityDisplay.label}
                          </span>
                        </div>
                        {!read && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-primary-light dark:bg-primary-dark rounded-full animate-pulse"></div>
                            <span className="text-xs font-medium text-light-status-info-text dark:text-blue-300">
                              New
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {short_description && (
                    <div
                      className={`mb-4 text-sm line-clamp-3 mt-2 ${
                        read
                          ? "text-light-text-secondary dark:text-gray-300"
                          : "text-light-status-info-text/80 dark:text-blue-200/80"
                      }`}
                    >
                      {short_description}
                    </div>
                  )}
                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-4">
                    {success && hasValidEntityId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick({
                            entity_id,
                            entity_type,
                            read,
                            id,
                            type,
                          });
                        }}
                        className="inline-flex items-center gap-2 px-2.5 py-1.5 text-sm font-medium text-white bg-primary-light dark:bg-primary-dark hover:bg-primary-hover dark:hover:bg-primary-hover rounded-lg transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-primary-light/50 dark:focus:ring-primary-dark/50 focus:outline-none"
                      >
                        <HiEye className="w-4 h-4" />
                        View Details
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(id);
                      }}
                      className={`inline-flex items-center gap-2 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 focus:ring-2 focus:outline-none ${
                        read
                          ? "text-light-text-secondary dark:text-gray-300 bg-light-surface dark:bg-tertiary-dark hover:bg-light-interactive-hover dark:hover:bg-gray-600 border border-light-border-default dark:border-gray-600 focus:ring-primary-light/50"
                          : "text-light-status-info-text dark:text-blue-200 bg-light-status-info-bg dark:bg-blue-900/30 hover:bg-light-interactive-hover dark:hover:bg-blue-900/50 border border-light-status-info-border dark:border-blue-800/50 focus:ring-primary-light/50"
                      }`}
                    >
                      <HiX className="w-4 h-4" />
                      Dismiss
                    </button>
                  </div>
                </div>

                {/* Time and delete button */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="flex items-center gap-1.5 text-xs text-light-text-tertiary dark:text-gray-400">
                    <HiClock className="w-3 h-3" />
                    <span>
                      {formattedTime} | {formattedDate}
                    </span>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-light-text-muted dark:text-gray-500 hover:text-light-status-error-text dark:hover:text-red-400 hover:bg-light-status-error-bg dark:hover:bg-red-900/20 transition-all duration-200"
                    aria-label="Delete notification"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remove the simple hover effect overlay since we're using background color changes now */}
    </div>
  );
};